import {t, Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {DeleteCardConfirmationDialog} from '../../../../libs/contacts-manager/src/lib/ui/web/dialogs/deleteContactConfirmationDialog';
import {
  AsyncHandler,
  AsyncHandlerOf,
  Handler,
  HandlerOf,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {buildAccountPublicLink} from '../../../helpers/accountHelpers';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {computeMergeCardDetails} from '../../../helpers/models/cardHelpers';
import {globalNamespace, isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {updateDataAction} from '../../../store/data/dataGroupsActions';
import {updateContactDetailsPanelSelectedContactAction} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {
  ContactDetailsCreateAccountSourceActionsEnum,
  ContactDetailsPanelSubState,
  subStateIsAccountDetailsSubState,
  subStateIsCreateAccountSubState,
  subStateIsMergeSubState,
} from '../../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {DropdownCategory} from '../../common/dropdowns/dropdownCategory';
import {DropdownItemIcon} from '../../common/dropdowns/dropdownItemIcon';
import {SearchContactsDropdownContainer} from '../../common/dropdowns/search/searchContactsDropdownContainer';
import {Skeleton} from '../../common/skeleton';
import {SidePanelHeader} from '../../inboxes/side/sidePanelHeader';
import {ContactDetailsVersionsEnum, useContactDetailsContext} from '../contactDetailsContext';
import {
  buildClickTrackingPayload,
  ContactDetailsClickActionsEnum,
  ContactDetailsClickLocationsEnum,
} from '../contactDetailsTrackingHelpers';
import {ContactMergeBanners} from '../contactMerge/contactMergeBanners';
import {ContactPluginHeaderContactContainer} from './contactPluginHeaderContactContainer';
import {ContactPluginHeaderTabsContainer} from './contactPluginHeaderTabsContainer';

const denseDropdownWidth = 260;
const moreDropdownWidth = 200;

/*
 * Props.
 */

interface ContactPluginHeaderProps {
  selectedVisualContact: VisualContactModel | undefined;
  sortedPartitionedContacts: {
    external: ReadonlyArray<VisualContactModel>;
    team: ReadonlyArray<VisualContactModel>;
  };
  selectedCard: CardModel | undefined;
  isLoading: boolean;
  subState?: ContactDetailsPanelSubState;
  host: string;
  account?: AccountModel;
  shouldShowDeleteAction?: boolean;
  isMergeInfoBannerVisible: boolean;

  createNewCardAsync: AsyncHandler<CardModel | undefined>;
  openContactDetailsPopUp: HandlerOf<number>;
  onDeleteCardAndCloseContactDetails: (panelId: ContactDetailsPanelId, cardId: number) => void;
  onClose?: Handler;
  onDelete?: Handler;
  selectContactTab: Handler;
  selectAccountTab: Handler;
  onToggleMergeInfoBanner: Handler;
}

interface StyledButtonContentDivProps {
  $isPopoverOpen: boolean;
}

/*
 * Styles.
 */

const StyledButtonContentDiv = styled.div<StyledButtonContentDivProps>`
  display: flex;
  flex-flow: row;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 7px 8px;
  margin: 12px -8px 12px -8px;

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
  }
  ${(p) =>
    p.$isPopoverOpen &&
    css`
      background: ${p.theme.greys.shade30};
    `}
`;

const StyledIcon = styled(Icon)`
  flex: 0 0 16px;
`;

const StyledContactSelectorTitleDiv = styled.div`
  flex: 1;
  color: ${(p) => p.theme.greys.shade80};
  padding: 0px 8px;
`;

const StyledDropdown = styled(Dropdown)`
  overflow: auto;
`;

const StyledDropdownList = styled(DropdownList)`
  padding-top: unset;
  overflow: visible;
`;

const StyledIconButton = styled(IconButton)`
  margin-right: 4px;
`;

const StyledPluginHeaderContainerDiv = styled.div`
  overflow: hidden;
`;

const StyledContactSwitcherContentDiv = styled.div`
  display: flex;
  margin: 0 -2px;
  padding-top: 1px;
`;

const StyledDenseSwitcherButton = styled(Button)`
  width: 58px;
  height: 30px;
  margin: 20px -4px 0 auto;
`;

const StyledContactIcon = styled(Icon)`
  margin-right: 4px;
`;

const StyledLoadingTabsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
  padding: 20px 0 18px;
`;

/*
 * Component.
 */

export const ContactPluginHeader: FC<ContactPluginHeaderProps> = (props) => {
  const {
    sortedPartitionedContacts,
    selectedVisualContact,
    selectedCard,
    isLoading,
    subState,
    host,
    account,
    shouldShowDeleteAction,
    isMergeInfoBannerVisible,
    onDeleteCardAndCloseContactDetails,
    createNewCardAsync,
    openContactDetailsPopUp,
    onClose,
    onDelete,
    selectContactTab,
    selectAccountTab,
    onToggleMergeInfoBanner,
  } = props;
  const reduxDispatch = useAppDispatch();
  const localize = useLocalize();

  const {version, isPopup, mergeContext, panelId, shouldHideHeaderTabs} = useContactDetailsContext();
  const namespace = selectedCard?.namespace || selectedVisualContact?.namespace || globalNamespace;
  const [selectorButtonRef, {width: selectorButtonWidth}] = useMeasureElement();
  const [headerPanelRef, {width: headerPanelWidth}] = useMeasureElement();
  const {copy} = useClipboard();

  const [isShowingMergeDropdown, setIsShowingMergeDropdown] = useState(false);
  const [isShowingDeleteDialog, setIsShowingDeleteDialog] = useState(false);
  const [hasTriedCreatingCard, setHasTriedCreatingCard] = useState(false);

  const shouldShowCopyAccountLinkAction = !shouldHideHeaderTabs;

  useEffect(() => {
    async function createCardAndUpdateStateAsync() {
      // If we have no selected card, we should create one in the background.
      if (isShowingMergeDropdown && !selectedCard && !hasTriedCreatingCard) {
        setHasTriedCreatingCard(true);
        await createNewCardAsync();
      }
    }
    createCardAndUpdateStateAsync();
  }, [selectedCard, isShowingMergeDropdown, hasTriedCreatingCard, createNewCardAsync]);

  const openPopupWindow = () => {
    openContactDetailsPopUp(headerPanelWidth);
    if (selectedCard) {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
        buildClickTrackingPayload(
          ContactDetailsClickActionsEnum.POPOUT,
          ContactDetailsClickLocationsEnum.HEADER,
          selectedCard,
        ),
      );
    }
  };

  const onMergeConfirm = async (mergeCard: CardModel) => {
    // If we do not have a selected card, return.
    if (!selectedCard) {
      return;
    }

    // Add the selected card to the data state.
    reduxDispatch(updateDataAction({cards: [mergeCard]}));

    const {mainCardId, mergeCardIds} = computeMergeCardDetails(selectedCard, mergeCard);
    mergeContext.startMerge(mainCardId, mergeCardIds);
  };

  const changeSelectedContact = useCallback(
    (contact: VisualContactModel) => {
      // If the contacts are the same, do not try to fetch again
      if (!selectedVisualContact || contact.contact.uid === selectedVisualContact.contact.uid) {
        return;
      }
      reduxDispatch(updateContactDetailsPanelSelectedContactAction(panelId, contact.contact.uid));
    },
    [reduxDispatch, panelId, selectedVisualContact],
  );

  const maybeRenderVersion1HeaderOptions = () => {
    if (isLoading || version !== ContactDetailsVersionsEnum.VERSION_1) {
      return null;
    }
    if (mergeContext.isMerging) {
      return <ContactMergeBanners />;
    }
    return maybeRenderContactSwitcher(
      selectedVisualContact,
      sortedPartitionedContacts,
      selectorButtonWidth,
      selectorButtonRef,
      changeSelectedContact,
    );
  };

  const maybeRenderVersion2HeaderOptions = () => {
    if (version !== ContactDetailsVersionsEnum.VERSION_2) {
      return null;
    }

    if (isLoading && !shouldHideHeaderTabs) {
      return (
        <StyledLoadingTabsContainerDiv>
          <Skeleton width="88px" height="16px" />
          <Skeleton width="88px" height="16px" />
        </StyledLoadingTabsContainerDiv>
      );
    }

    if (mergeContext.isMerging) {
      return <ContactMergeBanners />;
    }

    // Do not show tabs when creating an account
    if (subState && subStateIsCreateAccountSubState(subState)) {
      return null;
    }

    // If we should disabled the tabs completely or have no visual contact.
    if (shouldHideHeaderTabs || !selectedVisualContact) {
      return null;
    }

    return (
      <ContactPluginHeaderTabsContainer
        panelId={panelId}
        account={account}
        namespace={namespace}
        selectedCard={selectedCard}
        selectedVisualContact={selectedVisualContact}
        selectContactTab={selectContactTab}
        selectAccountTab={selectAccountTab}
        subState={subState}
        onRenderContactSwitcher={() =>
          maybeRenderContactSwitcher(
            selectedVisualContact,
            sortedPartitionedContacts,
            selectorButtonWidth,
            selectorButtonRef,
            changeSelectedContact,
            true,
          )
        }
      />
    );
  };

  return (
    <StyledPluginHeaderContainerDiv ref={headerPanelRef}>
      <SidePanelHeader
        title={renderPluginTitle(subState)}
        additionalActionsBefore={renderAdditionalHeaderBeforeActions(
          subState,
          mergeContext.cancelMerge,
          selectContactTab,
          selectAccountTab,
        )}
        additionalActionsAfter={renderAdditionalHeaderAfterActions(
          version,
          account,
          selectedCard,
          mergeContext.isMerging,
          isMergeInfoBannerVisible,
          isPopup,
          isLoading,
          host,
          isShowingMergeDropdown,
          subState,
          shouldShowDeleteAction || false,
          shouldShowCopyAccountLinkAction,
          selectedVisualContact,
          onMergeConfirm,
          onToggleMergeInfoBanner,
          openPopupWindow,
          localize,
          copy,
          setIsShowingMergeDropdown,
          setIsShowingDeleteDialog,
        )}
        shouldHideCloseButton={
          mergeContext.isMerging || (subState && subStateIsCreateAccountSubState(subState))
        }
        onClose={() => {
          if (onClose) {
            onClose();
            return;
          }
          if (selectedCard) {
            trackInterfaceEvent(
              InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
              buildClickTrackingPayload(
                ContactDetailsClickActionsEnum.CLOSE,
                ContactDetailsClickLocationsEnum.HEADER,
                selectedCard,
              ),
            );
          }
        }}
      >
        {maybeRenderVersion1HeaderOptions()}
        {maybeRenderVersion2HeaderOptions()}
      </SidePanelHeader>
      {isShowingDeleteDialog && selectedCard && (
        <DeleteCardConfirmationDialog
          card={selectedCard}
          onClose={() => setIsShowingDeleteDialog(false)}
          onDelete={() => {
            onDeleteCardAndCloseContactDetails(panelId, selectedCard.id);
            if (onDelete) {
              onDelete();
            }
          }}
        />
      )}
    </StyledPluginHeaderContainerDiv>
  );
};

const maybeRenderContactSwitcher = (
  selectedVisualContact: VisualContactModel | undefined,
  sortedPartitionedContacts: {
    external: ReadonlyArray<VisualContactModel>;
    team: ReadonlyArray<VisualContactModel>;
  },
  selectorButtonWidth: number,
  selectorButtonRef: (instance: HTMLDivElement | null) => void,
  changeSelectedContact: HandlerOf<VisualContactModel>,
  isDense = false,
) => {
  const contactsTotal = sortedPartitionedContacts.external.length + sortedPartitionedContacts.team.length;
  if (contactsTotal === 1 || !selectedVisualContact) {
    return null;
  }
  return (
    <PopoverCoordinator
      interactionId="contact-details-plugin-header-switcher-btn"
      renderButton={(isOpen) =>
        renderContactsSwitcherButton(contactsTotal, selectorButtonRef, isDense, isOpen)
      }
      renderModal={(popoverProps) => (
        <StyledDropdown
          {...popoverProps}
          placement={isDense ? 'bottom-end' : 'bottom'}
          width={isDense ? denseDropdownWidth : selectorButtonWidth}
        >
          {maybeRenderContacts(
            popoverProps,
            <Trans id="contact-details-plugin-header-external">External</Trans>,
            sortedPartitionedContacts.external,
            selectedVisualContact,
            changeSelectedContact,
            sortedPartitionedContacts.team.length > 0,
          )}
          {maybeRenderContacts(
            popoverProps,
            <Trans id="contact-details-plugin-header-team">Team</Trans>,
            sortedPartitionedContacts.team,
            selectedVisualContact,
            changeSelectedContact,
            sortedPartitionedContacts.external.length > 0,
            true,
          )}
        </StyledDropdown>
      )}
    />
  );
};

const renderPluginTitle = (subState?: ContactDetailsPanelSubState) => {
  if (subState && subStateIsMergeSubState(subState)) {
    return <Trans id="contact-details-plugin-header-merge-title">Review your merge</Trans>;
  }

  if (subState && subStateIsCreateAccountSubState(subState)) {
    if (subState.source === ContactDetailsCreateAccountSourceActionsEnum.CONTACT_DETAILS) {
      return <Trans id="contact-details-plugin-header-new-account-title">New account</Trans>;
    }
    if (subState.source === ContactDetailsCreateAccountSourceActionsEnum.ACCOUNT_DETAILS) {
      return <Trans id="contact-details-plugin-header-add-to-new-account-title">Add to new account</Trans>;
    }
  }

  return <Trans id="contact-details-plugin-header-title">Contact Details</Trans>;
};

const renderAdditionalHeaderAfterActions = (
  version: ContactDetailsVersionsEnum,
  account: AccountModel | undefined,
  selectedCard: CardModel | undefined,
  isMerging: boolean,
  shouldShowMergeInfoBanner: boolean,
  isInPopup: boolean,
  isLoading: boolean,
  host: string,
  isShowingMergeDropdown: boolean,
  subState: ContactDetailsPanelSubState | undefined,
  shouldShowDeleteAction: boolean,
  shouldShowCopyAccountLinkAction: boolean,
  selectedVisualContact: VisualContactModel | undefined,
  onMergeConfirm: HandlerOf<CardModel>,
  toggleMergeInfoBanner: Handler,
  openContactDetailsPopUp: Handler,
  localize: Localizer,
  copy: AsyncHandlerOf<string>,
  setIsShowingMergeDropdown: HandlerOf<boolean>,
  setIsShowingDeleteDialog: HandlerOf<boolean>,
): React.ReactElement => {
  // If the panel is currently loading or creating account, do not show actions
  if (isLoading || (subState && subStateIsCreateAccountSubState(subState))) {
    // TODO: Return null?
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  // If we are merging, we will need to add an information toggle
  if (isMerging) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName={shouldShowMergeInfoBanner ? 'infoFilled' : 'info'}
            color={shouldShowMergeInfoBanner ? theme.palette.blue.shade40 : theme.greys.shade80}
            label={<Trans id="contact-details-plugin-header-merge-info-toggle">Toggle merge info</Trans>}
            onClick={toggleMergeInfoBanner}
            hoverColor={shouldShowMergeInfoBanner ? undefined : theme.greys.shade80}
          />
        )}
      </UserThemeConsumer>
    );
  }

  const isSelectedCardPrivate = Boolean(selectedCard && isTeammateNamespace(selectedCard.namespace));

  return (
    <>
      {renderAdditionalActions(
        version,
        account,
        selectedCard,
        isSelectedCardPrivate,
        host,
        isShowingMergeDropdown,
        shouldShowDeleteAction,
        shouldShowCopyAccountLinkAction,
        selectedVisualContact,
        copy,
        localize,
        onMergeConfirm,
        setIsShowingMergeDropdown,
        setIsShowingDeleteDialog,
      )}
      {!isInPopup && selectedVisualContact && (
        <UserThemeConsumer>
          {(theme) => (
            <StyledIconButton
              iconName="popout"
              label={
                <Trans id="contact-details-plugin-header-view-in-external-window">
                  View in external window
                </Trans>
              }
              onClick={openContactDetailsPopUp}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          )}
        </UserThemeConsumer>
      )}
    </>
  );
};

const renderAdditionalActions = (
  version: ContactDetailsVersionsEnum,
  account: AccountModel | undefined,
  selectedCard: CardModel | undefined,
  isSelectedCardPrivate: boolean,
  host: string,
  isShowingMergeDropdown: boolean,
  shouldShowDeleteAction: boolean,
  shouldShowCopyAccountLinkAction: boolean,
  selectedVisualContact: VisualContactModel | undefined,
  copy: AsyncHandlerOf<string>,
  localize: Localizer,
  onMergeConfirm: HandlerOf<CardModel>,
  setIsShowingMergeDropdown: HandlerOf<boolean>,
  setIsShowingDeleteDialog: HandlerOf<boolean>,
) => {
  // We only want to show the 3 dots dropdown when on version 2
  if (version === ContactDetailsVersionsEnum.VERSION_2) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <PopoverCoordinator
            onPopoverToggle={(isOpen) => {
              if (!isOpen) {
                setIsShowingMergeDropdown(false);
              }
            }}
            renderButton={(isOpen) => (
              <StyledIconButton
                iconName="moreVertical"
                size={VisualSizesEnum.MEDIUM}
                label={<Trans id="contact-details-plugin-header-more">More</Trans>}
                color={theme.greys.shade80}
                hoverColor={theme.greys.shade80}
                hasHover={isOpen}
              />
            )}
            renderModal={(popoverProps) =>
              isShowingMergeDropdown ? (
                renderMergePopover(
                  selectedCard,
                  isSelectedCardPrivate,
                  localize,
                  (card) => {
                    setIsShowingMergeDropdown(false);
                    onMergeConfirm(card);
                  },
                  popoverProps,
                )
              ) : (
                <Dropdown {...popoverProps} placement="bottom-start" width={moreDropdownWidth}>
                  <DropdownList {...popoverProps}>
                    {selectedVisualContact && (
                      <DropdownItem
                        onSelect={(event) => {
                          event.preventDefault();
                          setIsShowingMergeDropdown(true);

                          if (selectedCard) {
                            trackInterfaceEvent(
                              InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
                              buildClickTrackingPayload(
                                ContactDetailsClickActionsEnum.MERGE,
                                ContactDetailsClickLocationsEnum.HEADER,
                                selectedCard,
                              ),
                            );
                          }
                        }}
                      >
                        <DropdownItemIcon
                          iconName="merge"
                          iconSize={16}
                          label={renderMergeContactWithText()}
                        />
                        <DropdownItemContent>{renderMergeContactWithText()}</DropdownItemContent>
                      </DropdownItem>
                    )}
                    {shouldShowCopyAccountLinkAction && account && (
                      <DropdownItem
                        onSelect={() => {
                          copy(buildAccountPublicLink(host, account.id));

                          if (selectedCard) {
                            trackInterfaceEvent(
                              InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
                              buildClickTrackingPayload(
                                ContactDetailsClickActionsEnum.COPY_ACCOUNT_LINK,
                                ContactDetailsClickLocationsEnum.HEADER,
                                selectedCard,
                              ),
                            );
                          }
                        }}
                      >
                        <DropdownItemIcon
                          iconName="linkSmall"
                          iconSize={16}
                          label={renderCopyAccountLinkText()}
                        />
                        <DropdownItemContent>{renderCopyAccountLinkText()}</DropdownItemContent>
                      </DropdownItem>
                    )}
                    {shouldShowDeleteAction && (
                      <DropdownItem
                        onSelect={(event) => {
                          event.preventDefault();
                          setIsShowingDeleteDialog(true);
                        }}
                      >
                        <DropdownItemIcon iconName="trash" iconSize={16} label={renderDeleteContactText()} />
                        <DropdownItemContent>{renderDeleteContactText()}</DropdownItemContent>
                      </DropdownItem>
                    )}
                  </DropdownList>
                </Dropdown>
              )
            }
          />
        )}
      </UserThemeConsumer>
    );
  }
  return renderMergePopover(selectedCard, isSelectedCardPrivate, localize, onMergeConfirm);
};

const renderMergePopover = (
  selectedCard: CardModel | undefined,
  isSelectedCardPrivate: boolean,
  localize: Localizer,
  onMergeConfirm: HandlerOf<CardModel>,
  containerPopoverProps?: PopoverTransferProps,
) => {
  const placeholderText = localize(t('contacts-merge-contact-placeholder')`Search for a contact to merge`);

  if (containerPopoverProps) {
    return (
      <SearchContactsDropdownContainer
        popoverProps={containerPopoverProps}
        searchPlaceholder={placeholderText}
        filterSearchResults={(card) => {
          // Remove the currently selected card in P4
          const notSelected = !selectedCard || card.id !== selectedCard.id;
          // Remove Team/Company cards when they current have a private card selected
          const correctSelectedCard = !isSelectedCardPrivate || isTeammateNamespace(card.namespace);

          return notSelected && correctSelectedCard;
        }}
        onConfirm={onMergeConfirm}
      />
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <PopoverCoordinator
          renderButton={() => (
            <StyledIconButton
              interactionId="contact-details-merge-contact-btn"
              iconName="merge"
              label={<Trans id="contact-details-plugin-header-merge">Merge with another contact</Trans>}
              onClick={() => {
                if (selectedCard) {
                  trackInterfaceEvent(
                    InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
                    buildClickTrackingPayload(
                      ContactDetailsClickActionsEnum.MERGE,
                      ContactDetailsClickLocationsEnum.HEADER,
                      selectedCard,
                    ),
                  );
                }
              }}
              color={theme.greys.shade70}
              hoverColor={theme.greys.shade80}
            />
          )}
          renderModal={(popoverProps) => (
            <SearchContactsDropdownContainer
              popoverProps={popoverProps}
              searchPlaceholder={placeholderText}
              filterSearchResults={(card) => {
                // Remove the currently selected card in P4
                const notSelected = !selectedCard || card.id !== selectedCard.id;
                // Remove Team/Company cards when they current have a private card selected
                const correctSelectedCard = !isSelectedCardPrivate || isTeammateNamespace(card.namespace);

                return notSelected && correctSelectedCard;
              }}
              onConfirm={onMergeConfirm}
            />
          )}
        />
      )}
    </UserThemeConsumer>
  );
};

const renderAdditionalHeaderBeforeActions = (
  subState: ContactDetailsPanelSubState | undefined,
  onMergeCancel: Handler,
  selectContactTab: Handler,
  selectAccountTab: Handler,
): React.ReactElement => {
  if (!subState || subStateIsAccountDetailsSubState(subState)) {
    // TODO: Return null?
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="arrowBack"
          color={theme.greys.shade80}
          label={<Trans id="contact-details-plugin-header-back">Back</Trans>}
          onClick={() => {
            if (subStateIsMergeSubState(subState)) {
              onMergeCancel();
            }

            if (subStateIsCreateAccountSubState(subState)) {
              if (subState.source === ContactDetailsCreateAccountSourceActionsEnum.CONTACT_DETAILS) {
                selectAccountTab();
              } else {
                selectContactTab();
              }
            }
          }}
        />
      )}
    </UserThemeConsumer>
  );
};

/*
 * Helpers.
 */

const renderContactsSwitcherButton = (
  contactsTotal: number,
  selectorButtonRef: (instance: HTMLDivElement | null) => void,
  isDense: boolean,
  isOpen: boolean,
) => {
  if (isDense) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledDenseSwitcherButton hasHover={isOpen} ref={selectorButtonRef}>
            <StyledContactSwitcherContentDiv>
              <StyledContactIcon name="contactsFilledSmall" color={theme.greys.shade70} />
              <Icon name="chevronDownSmall" color={theme.greys.shade80} />
            </StyledContactSwitcherContentDiv>
          </StyledDenseSwitcherButton>
        )}
      </UserThemeConsumer>
    );
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledButtonContentDiv $isPopoverOpen={isOpen} ref={selectorButtonRef}>
          <StyledIcon name="contactsFilledSmall" color={theme.greys.shade70} />
          <StyledContactSelectorTitleDiv>Switch Contact ({contactsTotal})</StyledContactSelectorTitleDiv>
          <StyledIcon name="chevronDownSmall" color={theme.greys.shade60} />
        </StyledButtonContentDiv>
      )}
    </UserThemeConsumer>
  );
};

const maybeRenderContacts = (
  popoverProps: PopoverTransferProps,
  title: React.ReactNode,
  contacts: ReadonlyArray<VisualContactModel>,
  selectedVisualContact: VisualContactModel,
  onSelectedContactChange: HandlerOf<VisualContactModel>,
  shouldRenderTitle: boolean,
  isTeammate?: boolean,
) => {
  if (contacts.length === 0) {
    return null;
  }

  const contactsRender = contacts.map((contact) => (
    <ContactPluginHeaderContactContainer
      key={contact.contact.uid}
      contact={contact}
      onSelectedContactChange={onSelectedContactChange}
      isSelected={selectedVisualContact.contact.uid === contact.contact.uid}
      isTeammate={isTeammate}
    />
  ));
  return (
    <>
      {shouldRenderTitle && <DropdownCategory>{title}</DropdownCategory>}
      <StyledDropdownList {...popoverProps}>{contactsRender}</StyledDropdownList>
    </>
  );
};

function renderCopyAccountLinkText() {
  return <Trans id="contact-details-plugin-header-copy-account-link">Copy account link</Trans>;
}

function renderMergeContactWithText() {
  return <Trans id="contact-details-plugin-header-merge-contact">Merge contact with...</Trans>;
}

function renderDeleteContactText() {
  return <Trans id="contact-details-plugin-header-delete-contact">Delete</Trans>;
}
